import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';

const TermsOfService = () => {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Terms of Service - ORIN';
  }, []);

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-card rounded-lg shadow-lg border border-border p-8">
          <div className="mb-6">
            <Button
              variant="outline"
              onClick={() => navigate('/login')}
              iconName="ArrowLeft"
            >
              Back to Login
            </Button>
          </div>

          <div className="prose prose-sm max-w-none">
            <h1 className="text-3xl font-heading font-bold text-foreground mb-2">Terms of Service</h1>
            <p className="text-sm text-muted-foreground mb-6">
              <strong>ORIN – Decision Intelligence System</strong><br />
              Effective Date: 1/1/2025<br />
              Last Updated: 9/1/2025
            </p>

            <p className="text-foreground mb-6">
              These Terms of Service ("Terms") govern access to and use of the ORIN Decision Intelligence System ("ORIN" or the "Platform"), developed and operated by American Logics LLC ("American Logics," "we," "our," or "us").
            </p>
            <p className="text-foreground mb-6">
              By accessing or using ORIN, you agree to these Terms.
            </p>

            <h2 className="text-2xl font-heading font-semibold text-foreground mt-8 mb-4">1. Eligibility & Authorized Use</h2>
            <p className="text-foreground mb-2">ORIN is intended for business and enterprise use only.</p>
            <p className="text-foreground mb-2">Access to ORIN is permitted only to:</p>
            <ul className="list-disc pl-6 mb-4 text-foreground space-y-1">
              <li>Authorized users designated by a client organization</li>
              <li>Administrators approved by American Logics or the client organization</li>
            </ul>
            <p className="text-foreground mb-2">Users must:</p>
            <ul className="list-disc pl-6 mb-6 text-foreground space-y-1">
              <li>Be at least 18 years old</li>
              <li>Use the Platform solely for lawful business purposes</li>
            </ul>

            <h2 className="text-2xl font-heading font-semibold text-foreground mt-8 mb-4">2. License & Access Rights</h2>
            <p className="text-foreground mb-4">
              Subject to compliance with these Terms and applicable agreements, American Logics grants a:
            </p>
            <p className="text-foreground mb-4">
              Limited, non-exclusive, non-transferable, revocable license to access and use ORIN for internal business purposes.
            </p>
            <p className="text-foreground mb-6">No ownership rights are transferred.</p>

            <h2 className="text-2xl font-heading font-semibold text-foreground mt-8 mb-4">3. Account Responsibilities</h2>
            <p className="text-foreground mb-2">Users are responsible for:</p>
            <ul className="list-disc pl-6 mb-4 text-foreground space-y-1">
              <li>Maintaining confidentiality of login credentials</li>
              <li>Ensuring accurate account information</li>
              <li>All activity conducted under their account</li>
            </ul>
            <p className="text-foreground mb-6">You must immediately notify us of any unauthorized access.</p>

            <h2 className="text-2xl font-heading font-semibold text-foreground mt-8 mb-4">4. Client Data & Ownership</h2>
            
            <h3 className="text-xl font-heading font-semibold text-foreground mt-6 mb-3">A. Client Ownership</h3>
            <p className="text-foreground mb-4">
              All data uploaded to ORIN by a client organization ("Client Data") remains the property of the client.
            </p>
            <p className="text-foreground mb-6">American Logics does not claim ownership of Client Data.</p>

            <h3 className="text-xl font-heading font-semibold text-foreground mt-6 mb-3">B. Processing Role</h3>
            <p className="text-foreground mb-2">American Logics acts as:</p>
            <ul className="list-disc pl-6 mb-4 text-foreground space-y-1">
              <li>Data Processor for Client Data</li>
              <li>Data Controller for account and system metadata</li>
            </ul>
            <p className="text-foreground mb-6">Data processing is subject to contractual agreements between American Logics and the client organization.</p>

            <h2 className="text-2xl font-heading font-semibold text-foreground mt-8 mb-4">5. Acceptable Use</h2>
            <p className="text-foreground mb-2">Users agree not to:</p>
            <ul className="list-disc pl-6 mb-4 text-foreground space-y-1">
              <li>Reverse engineer, decompile, or copy the Platform</li>
              <li>Interfere with system security</li>
              <li>Upload malicious code</li>
              <li>Use ORIN for unlawful or fraudulent purposes</li>
              <li>Attempt unauthorized data access</li>
            </ul>
            <p className="text-foreground mb-6">Violation may result in immediate suspension or termination.</p>

            <h2 className="text-2xl font-heading font-semibold text-foreground mt-8 mb-4">6. Intellectual Property</h2>
            <p className="text-foreground mb-2">All rights, title, and interest in ORIN, including:</p>
            <ul className="list-disc pl-6 mb-4 text-foreground space-y-1">
              <li>Software architecture</li>
              <li>Algorithms</li>
              <li>Data models</li>
              <li>Dashboards</li>
              <li>Branding</li>
              <li>Trade secrets</li>
            </ul>
            <p className="text-foreground mb-6">are the exclusive property of American Logics LLC. Unauthorized reproduction or redistribution is prohibited.</p>

            <h2 className="text-2xl font-heading font-semibold text-foreground mt-8 mb-4">7. Confidentiality</h2>
            <p className="text-foreground mb-2">Users agree to maintain confidentiality of:</p>
            <ul className="list-disc pl-6 mb-4 text-foreground space-y-1">
              <li>Proprietary analytics methodologies</li>
              <li>Platform structure</li>
              <li>System architecture</li>
              <li>Pricing and licensing information</li>
            </ul>
            <p className="text-foreground mb-6">This obligation survives termination.</p>

            <h2 className="text-2xl font-heading font-semibold text-foreground mt-8 mb-4">8. Service Availability</h2>
            <p className="text-foreground mb-4">
              American Logics strives for high availability but does not guarantee uninterrupted service.
            </p>
            <p className="text-foreground mb-2">We may:</p>
            <ul className="list-disc pl-6 mb-6 text-foreground space-y-1">
              <li>Perform maintenance</li>
              <li>Upgrade systems</li>
              <li>Modify features</li>
              <li>Suspend service for security reasons</li>
            </ul>

            <h2 className="text-2xl font-heading font-semibold text-foreground mt-8 mb-4">9. Third-Party Integrations</h2>
            <p className="text-foreground mb-4">
              ORIN may integrate with third-party systems (e.g., cloud providers, data connectors).
            </p>
            <p className="text-foreground mb-2">American Logics is not responsible for:</p>
            <ul className="list-disc pl-6 mb-6 text-foreground space-y-1">
              <li>Third-party service interruptions</li>
              <li>Data inaccuracies from external integrations</li>
              <li>Changes made by third-party providers</li>
            </ul>

            <h2 className="text-2xl font-heading font-semibold text-foreground mt-8 mb-4">10. Disclaimer of Warranties</h2>
            <p className="text-foreground mb-4">
              ORIN is provided on an: "AS IS" and "AS AVAILABLE" basis.
            </p>
            <p className="text-foreground mb-2">To the fullest extent permitted by law, American Logics disclaims:</p>
            <ul className="list-disc pl-6 mb-4 text-foreground space-y-1">
              <li>Implied warranties of merchantability</li>
              <li>Fitness for a particular purpose</li>
              <li>Non-infringement</li>
              <li>Accuracy of predictive or analytical outputs</li>
            </ul>
            <p className="text-foreground mb-6">Decision-making remains the responsibility of the user organization.</p>

            <h2 className="text-2xl font-heading font-semibold text-foreground mt-8 mb-4">11. Limitation of Liability</h2>
            <p className="text-foreground mb-4">To the maximum extent permitted by law:</p>
            <p className="text-foreground mb-2">American Logics shall not be liable for:</p>
            <ul className="list-disc pl-6 mb-4 text-foreground space-y-1">
              <li>Indirect damages</li>
              <li>Lost profits</li>
              <li>Business interruption</li>
              <li>Data loss</li>
              <li>Consequential damages</li>
            </ul>
            <p className="text-foreground mb-6">Total liability shall not exceed the amount paid by the client in the preceding 12 months.</p>

            <h2 className="text-2xl font-heading font-semibold text-foreground mt-8 mb-4">12. Indemnification</h2>
            <p className="text-foreground mb-2">Users and client organizations agree to indemnify and hold harmless American Logics from claims arising from:</p>
            <ul className="list-disc pl-6 mb-6 text-foreground space-y-1">
              <li>Misuse of the Platform</li>
              <li>Violation of these Terms</li>
              <li>Unlawful data processing by the client</li>
            </ul>

            <h2 className="text-2xl font-heading font-semibold text-foreground mt-8 mb-4">13. Termination</h2>
            <p className="text-foreground mb-2">We may suspend or terminate access if:</p>
            <ul className="list-disc pl-6 mb-4 text-foreground space-y-1">
              <li>Terms are violated</li>
              <li>Security risk is identified</li>
              <li>Client agreement ends</li>
            </ul>
            <p className="text-foreground mb-6">Upon termination, access rights immediately cease.</p>

            <h2 className="text-2xl font-heading font-semibold text-foreground mt-8 mb-4">14. Governing Law</h2>
            <p className="text-foreground mb-6">
              These Terms shall be governed by the laws of: State of New Jersey, United States. Without regard to conflict of law principles.
            </p>

            <h2 className="text-2xl font-heading font-semibold text-foreground mt-8 mb-4">15. Modifications</h2>
            <p className="text-foreground mb-6">
              We may update these Terms periodically. Continued use of ORIN after updates constitutes acceptance.
            </p>

            <h2 className="text-2xl font-heading font-semibold text-foreground mt-8 mb-4">16. Contact Information</h2>
            <p className="text-foreground mb-2">
              <strong>American Logics LLC</strong><br />
              Email: <a href="mailto:legal@americanlogics.com" className="text-primary hover:text-primary/80">legal@americanlogics.com</a><br />
              Website: <a href="http://www.americanlogics.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80">www.americanlogics.com</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;